import { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';
import forEach from 'lodash/forEach';

import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';

import { locale } from 'src/config';
import {
  useGetFieldsCategory,
  useGetInternalAttributes,
} from 'src/pages/ProjectDetailsPage/api';
import { colors } from 'src/utils/customColors';
import { formatNumber } from 'src/utils/standards';
import { forceString } from 'src/utils/datetime';
import LoaderIndicator from 'src/components/LoaderIndicator';
import { useCreateCandidateAdditionalFields } from 'src/pages/CandidatesDetailsPage/api';

import messages from './messages';
import { CandidateTabProps } from '../CandidateShiftsTab';
import CandidateInfo from '../CandidateInfo/CandidateInfo';
import resolveAttributesDynamicField from '../AttributesFieldComponents';

export type FieldValueType = Record<
  string,
  Record<string, boolean | Array<string> | string | null>
>;

export default function CandidateAdditionalFieldsTab({
  uuid,
}: CandidateTabProps) {
  const { formatMessage } = useIntl();

  const [editMode, setEditMode] = useState(false);
  const [attributes, setAttributes] = useState<FieldValueType>({});

  const { data: fieldsCategory } = useGetFieldsCategory({
    enabled: Boolean(uuid),
  });

  const { data: internalAttributes, isPending: pendingFields } =
    useGetInternalAttributes({
      userUuid: uuid,
      fields: fieldsCategory!,
      enabled: Boolean(fieldsCategory) && Boolean(uuid),
    });

  const { mutate: createCandidateAdditionalFields, isPending: pendingSave } =
    useCreateCandidateAdditionalFields();

  const handleFieldChange = useCallback(
    (
      field: UCM.DynamicFieldType,
      value: Record<string, boolean | Array<string> | string | null>,
    ) => {
      setAttributes((prev) => ({
        ...prev,
        [field.uuid]: {
          ...prev[field.uuid],
          ...value,
        },
      }));
    },
    [],
  );

  const defaultValues = useMemo(() => {
    if (!internalAttributes) return {};
    const values: FieldValueType = {};
    forEach(internalAttributes, (field) => {
      if (!field) return;
      const typedField = field as unknown as UCM.DynamicFieldType;
      if (!typedField.values?.length) return;
      const type = typedField.type?.code;
      if (type === 'single-selectable' || type === 'multi-selectable') {
        const options =
          type === 'single-selectable'
            ? [typedField.values[0]?.option?.uuid]
            : typedField.values.map((v) => v.option?.uuid);
        values[typedField.uuid] = {
          options: options.filter((opt: unknown) => opt == 0 || opt),
        };
        return;
      }
      const value = typedField.values[0]?.answer;
      values[typedField.uuid] = { value };
    });
    return values;
  }, [internalAttributes]);

  const resolveUserDynamicField = useCallback(
    // eslint-disable-next-line
    (field: any) => {
      if (!field) return null;
      const { label, description, values, type } = field;
      const AnswerField = resolveAttributesDynamicField(field);

      const value = () => {
        if (!Array.isArray(values) || values.length === 0) return '-';

        switch (type.code) {
          case 'date':
            return (
              map(values, ({ answer, option }) => {
                const formattedString = forceString(answer);
                return formattedString === 'Invalid date' || !formattedString
                  ? answer || option?.inputValue
                  : formattedString;
              })
                .filter((a) => a)
                .join(', ') || '-'
            );
          case 'multi-selectable':
          case 'single-selectable':
            return (
              map(values, ({ option, answer }) => option?.inputValue || answer)
                .filter((a) => a)
                .join(', ') || '-'
            );
          case 'number':
            return (
              map(values, ({ answer, option }) => {
                const formattedString = formatNumber(answer, locale);
                return formattedString === 'NaN' || !formattedString
                  ? answer || option?.inputValue
                  : formattedString;
              })
                .filter((a) => a)
                .join(', ') || '-'
            );
          case 'boolean':
            return (
              map(values, ({ answer, option }) => {
                if (answer === 'true') return formatMessage(messages.yesLabel);
                if (answer === 'false') return formatMessage(messages.noLabel);
                return answer || option?.inputValue;
              })
                .filter((a) => a)
                .join(', ') || '-'
            );
          default:
            return (
              map(values, ({ answer, option }) => answer || option?.inputValue)
                .filter((a) => a)
                .join(', ') || '-'
            );
        }
      };

      return editMode ? (
        <AnswerField
          field={field}
          // @ts-ignore
          value={attributes[field.uuid] || defaultValues[field.uuid]}
          helperText={
            !field.isVisibleByUser
              ? formatMessage(messages.notVisibleByUserLabel)
              : undefined
          }
          multiple={field.type?.code === 'multi-selectable'}
          disabled={field.isVisibleByUser}
          onChange={handleFieldChange}
        />
      ) : (
        <CandidateInfo
          label={label}
          value={value()}
          description={description}
        />
      );
    },
    [editMode, attributes, defaultValues],
  );

  const getFields = useCallback(
    (uuid: string) => {
      const fields = internalAttributes?.filter(
        // @ts-ignore
        (field) => field?.category?.uuid === uuid,
      );
      return fields || [];
    },
    [internalAttributes],
  );

  const handleEditMode = useCallback(() => {
    setEditMode(true);
  }, []);

  const handleCancel = useCallback(() => {
    setEditMode(false);
    setAttributes({});
  }, []);

  const handleSave = useCallback(() => {
    createCandidateAdditionalFields(
      {
        uuid: uuid!,
        attributes,
      },
      {
        onSuccess: handleCancel,
      },
    );
  }, [uuid, attributes]);

  return (
    <>
      {(pendingFields || pendingSave) && <LoaderIndicator fullScreen />}
      <Grid
        container
        width="100%"
        justifyContent="space-between"
        mb={1}
        p={2}
        alignItems="center"
        bgcolor={editMode ? colors.lightYellowSecondary : 'transparent'}
      >
        <Typography variant="h4" fontSize={20}>
          {editMode
            ? formatMessage(messages.editTitle)
            : formatMessage(messages.title)}
        </Typography>
        {editMode ? (
          <Grid container spacing={2}>
            <Button variant="outlined" onClick={handleCancel}>
              {formatMessage(messages.cancelButton)}
            </Button>
            <Button
              color="primary"
              startIcon={<CheckOutlinedIcon />}
              onClick={handleSave}
            >
              {formatMessage(messages.saveButton)}
            </Button>
          </Grid>
        ) : (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<EditOutlinedIcon />}
            onClick={handleEditMode}
          >
            {formatMessage(messages.editButton)}
          </Button>
        )}
      </Grid>
      {!isEmpty(fieldsCategory) && (
        <Stack sx={{ overflowX: 'auto' }} p={2}>
          <Grid container spacing={4} width="100%">
            {map(fieldsCategory, (field) => {
              const fields = getFields(field?.value);
              return (
                <Grid key={field?.value} size={{ xs: 12, md: 3 }} spacing={4}>
                  <Typography variant="h4" fontSize={20}>
                    {field?.label}
                  </Typography>
                  {!isEmpty(fields) ? (
                    <Stack mt={4} mb={7}>
                      <Grid container spacing={4} width="100%">
                        {map(fields, (dynamicField) => (
                          <Grid key={dynamicField?.id} size={12}>
                            {resolveUserDynamicField(dynamicField)}
                          </Grid>
                        ))}
                      </Grid>
                    </Stack>
                  ) : (
                    <Typography fontSize={12} fontWeight={600}>
                      {'-'}
                    </Typography>
                  )}
                </Grid>
              );
            })}
          </Grid>
        </Stack>
      )}
    </>
  );
}
